import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowUp, ArrowDown, Upload } from "lucide-react";
import { api, assetUrl, formatApiError } from "@/lib/api";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export function AboutAdmin({ content, refresh }) {
  const [blocks, setBlocks] = useState([]);
  useEffect(() => { setBlocks(Array.isArray(content?.about_blocks) ? content.about_blocks : []); }, [content]);

  const set = (i, k, v) => setBlocks((b) => b.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setBlocks((b) => [...b, { headline: "", body: "", image: "" }]);
  const remove = (i) => setBlocks((b) => b.filter((_, idx) => idx !== i));
  const move = (i, d) => setBlocks((b) => {
    const j = i + d; if (j < 0 || j >= b.length) return b;
    const n = [...b]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const onImg = async (i, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const r = await uploadFile(f); set(i, "image", r.url); toast.success("Image added — remember to Save"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };
  const save = async () => {
    try { await api.put("/content", { about_blocks: blocks.filter((x) => x.headline || x.body || x.image) }); refresh(); toast.success("About Us saved & published"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="about-admin">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif-lux text-2xl">About Us — Story Sections ({blocks.length})</h3>
          <p className="text-xs text-[#8a8a80]">Add story sections (e.g. the founder's story) — each with a headline, photo and writing. They appear on the About Us page.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={add} className="btn-outline-dark !py-2 !px-4 text-sm inline-flex items-center" data-testid="about-add"><Plus size={14} className="mr-1" /> Add section</button>
          <button onClick={save} className="btn-gold !py-2 !px-5 text-sm inline-flex items-center" data-testid="about-save"><Save size={14} className="mr-1.5" /> Save</button>
        </div>
      </div>

      {blocks.map((b, i) => (
        <div key={i} className="bg-white border border-[#E5E5DF] p-4 flex flex-col md:flex-row gap-4" data-testid={`about-row-${i}`}>
          <div className="flex flex-col items-center gap-2">
            <div className="w-28 h-32 rounded-md overflow-hidden bg-[#F1EFE8] flex-shrink-0">
              {b.image && <img src={assetUrl(b.image)} alt="" className="w-full h-full object-cover" />}
            </div>
            <label className="btn-outline-dark !py-1.5 !px-3 cursor-pointer text-xs inline-flex items-center">
              <Upload size={12} className="mr-1" /> Photo
              <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => onImg(i, e)} data-testid={`about-img-${i}`} />
            </label>
          </div>
          <div className="flex-1 space-y-2">
            <input className="input-lux !py-2" placeholder="Headline (e.g. Meet Our Founder)" value={b.headline || ""} onChange={(e) => set(i, "headline", e.target.value)} data-testid={`about-headline-${i}`} />
            <textarea rows={5} className="input-lux !py-2 resize-none" placeholder="Story / writing" value={b.body || ""} onChange={(e) => set(i, "body", e.target.value)} data-testid={`about-body-${i}`} />
          </div>
          <div className="flex md:flex-col items-center gap-1">
            <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-[#8a8a80] hover:text-[#47562F] disabled:opacity-30" aria-label="Move up"><ArrowUp size={16} /></button>
            <button onClick={() => move(i, 1)} disabled={i === blocks.length - 1} className="p-1 text-[#8a8a80] hover:text-[#47562F] disabled:opacity-30" aria-label="Move down"><ArrowDown size={16} /></button>
            <button onClick={() => remove(i)} className="p-1 text-[#8a8a80] hover:text-red-600" aria-label="Delete" data-testid={`about-del-${i}`}><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
      {blocks.length === 0 && <p className="text-sm text-[#8a8a80]">No story sections yet. Click “Add section”, add a headline, photo and writing, then “Save”.</p>}
    </div>
  );
}
